import React, { useRef, useState } from 'react';
import { Text, Loader, Alert } from '@aws-amplify/ui-react';
import { uploadData } from 'aws-amplify/storage';

function FileUpload({ selectedDate, identityId, onUploadComplete }) {
  const fileInputRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [validationError, setValidationError] = useState(null);
  const [fileStatuses, setFileStatuses] = useState({});

  const extractDateFromFilename = (filename) => {
    // Match YYYY-MM-DD pattern at the start of filename
    const datePattern = /^(\d{4})-(\d{2})-(\d{2})/;
    const match = filename.match(datePattern);
    
    if (match) {
      const [, year, month, day] = match;
      const date = new Date(year, parseInt(month) - 1, parseInt(day));
      
      // Validate the date is valid
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
    
    return null;
  };

  const generateS3Key = (file, date) => {
    const year = date.getFullYear();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.toISOString().split('T')[0];
    return `${year}/${month}/${day}/${file.name}`;
  };

  const validateFile = (file) => {
    const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2GB
    const ALLOWED_TYPES = [
      'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/wave',
      'video/mp4', 'video/quicktime', 'video/x-msvideo',
      'audio/mp4', 'audio/x-m4a'
    ];
    const ALLOWED_EXTENSIONS = ['.mp3', '.wav', '.mp4', '.mov', '.m4a', '.avi'];

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: `File "${file.name}" is too large. Maximum size is 2GB.` };
    }

    // Check file type
    const fileExtension = file.name.toLowerCase().match(/\.[^.]+$/)?.[0];
    const isValidType = ALLOWED_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.includes(fileExtension);

    if (!isValidType) {
      return { valid: false, error: `File "${file.name}" is not a supported audio/video format.` };
    }

    return { valid: true };
  };

  const uploadFile = async (file) => {
    const fileId = `${file.name}-${Date.now()}`;

    try {
      // Try to extract date from filename, fallback to selectedDate
      const fileDate = extractDateFromFilename(file.name) || selectedDate;
      const s3Key = generateS3Key(file, fileDate);

      setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));
      setFileStatuses(prev => ({ ...prev, [fileId]: 'uploading' }));

      const uploadTask = uploadData({
        key: s3Key,
        data: file,
        options: {
          accessLevel: 'private',
          onProgress: ({ transferredBytes, totalBytes }) => {
            if (totalBytes) {
              const progress = Math.round((transferredBytes / totalBytes) * 100);
              setUploadProgress(prev => ({ ...prev, [fileId]: progress }));
            }
          }
        }
      });

      await uploadTask.result;
      console.log('File uploaded successfully to:', s3Key);

      setFileStatuses(prev => ({ ...prev, [fileId]: 'complete' }));

      setTimeout(() => {
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[fileId];
          return newProgress;
        });
        setFileStatuses(prev => {
          const newStatuses = { ...prev };
          delete newStatuses[fileId];
          return newStatuses;
        });
      }, 1000);

      return { success: true, fileName: file.name, date: fileDate };
    } catch (error) {
      console.error('Upload failed:', error);
      setFileStatuses(prev => ({ ...prev, [fileId]: 'error' }));
      setTimeout(() => {
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[fileId];
          return newProgress;
        });
        setFileStatuses(prev => {
          const newStatuses = { ...prev };
          delete newStatuses[fileId];
          return newStatuses;
        });
      }, 2000);
      return { success: false, fileName: file.name, error };
    }
  };

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;

    setValidationError(null);

    // Validate all files first
    const filesArray = Array.from(files);
    for (const file of filesArray) {
      const validation = validateFile(file);
      if (!validation.valid) {
        setValidationError(validation.error);
        setTimeout(() => setValidationError(null), 5000);
        return;
      }
    }

    // Check if any files have dates in their names
    const filesWithDates = filesArray.map(file => ({
      file,
      extractedDate: extractDateFromFilename(file.name)
    }));

    const hasFilesWithoutDates = filesWithDates.some(f => f.extractedDate === null);

    // If some files don't have dates and no date is selected, show error
    if (hasFilesWithoutDates && !selectedDate) {
      setValidationError('Please select a date or use files with date prefix (YYYY-MM-DD)');
      setTimeout(() => setValidationError(null), 5000);
      return;
    }

    setIsUploading(true);
    setUploadStatus(null);
    setUploadingFiles(filesArray.map(f => ({
      name: f.name,
      size: f.size,
      id: `${f.name}-${Date.now()}`
    })));

    const results = await Promise.all(
      filesArray.map(file => uploadFile(file))
    );

    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;
    const autoDatedCount = results.filter(r => r.success && r.date && extractDateFromFilename(r.fileName)).length;

    if (successCount > 0) {
      onUploadComplete();
    }

    if (failCount === 0) {
      setUploadStatus(autoDatedCount > 0 ? 'success-auto' : 'success');
    } else if (successCount === 0) {
      setUploadStatus('error');
    } else {
      setUploadStatus('partial');
    }

    setTimeout(() => {
      setUploadStatus(null);
      setUploadingFiles([]);
    }, 4000);

    setIsUploading(false);
  };

  const handleFileSelect = async (e) => {
    await handleFiles(e.target.files);
    e.target.value = '';
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    await handleFiles(files);
  };

  const handleClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  const progressValues = Object.values(uploadProgress);
  const averageProgress = progressValues.length > 0
    ? Math.round(progressValues.reduce((a, b) => a + b, 0) / progressValues.length)
    : 0;

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%' }}>
      <div
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className="upload-drop-zone"
        style={{
          border: `2px dashed ${isDragging ? '#047d95' : '#d1d5db'}`,
          borderRadius: '8px',
          padding: '2rem 1rem',
          textAlign: 'center',
          cursor: !isUploading ? 'pointer' : 'not-allowed',
          backgroundColor: isDragging ? '#f0f9ff' : '#fafafa',
          transition: 'all 0.3s ease',
          position: 'relative'
        }}
      >
        <div style={{
          fontSize: '3rem',
          marginBottom: '0.5rem',
          transition: 'transform 0.3s ease',
          transform: isDragging ? 'scale(1.2)' : 'scale(1)'
        }}>
          {isDragging ? '📂' : isUploading ? '⏳' : '☁️'}
        </div>
        <Text fontSize="1rem" fontWeight="600" color="#047d95">
          {isUploading ? 'Uploading files...' : isDragging ? 'Drop files here!' : 'Drag & drop your files'}
        </Text>
        <Text fontSize="0.75rem" color="#6b7280" marginTop="0.25rem">
          or click to browse
        </Text>
        <Text fontSize="0.7rem" color="#9ca3af" marginTop="0.75rem" fontStyle="italic">
          Supported: MP3, WAV, MP4, MOV (max 2GB)
        </Text>
        <Text fontSize="0.7rem" color="#9ca3af" marginTop="0.25rem" fontStyle="italic">
          💡 Files with YYYY-MM-DD prefix will be auto-sorted by date
        </Text>
      </div>
      
      {isUploading && (
        <div style={{ width: '100%' }}>
          <div style={{ marginBottom: '0.75rem' }}>
            <Loader
              variation="linear"
              percentage={averageProgress}
              isDeterminate={true}
              size="small"
              style={{ marginBottom: '0.5rem' }}
            />
            <Text fontSize="0.75rem" color="#047d95" textAlign="center" fontWeight="600">
              {uploadingFiles.length} file{uploadingFiles.length !== 1 ? 's' : ''} • {averageProgress}% complete
            </Text>
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            maxHeight: '200px',
            overflowY: 'auto',
            padding: '0.5rem',
            backgroundColor: '#f9fafb',
            borderRadius: '6px'
          }}>
            {uploadingFiles.map((file) => {
              const fileId = file.id;
              const progress = uploadProgress[fileId] || 0;
              const status = fileStatuses[fileId] || 'pending';

              return (
                <div
                  key={fileId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem',
                    backgroundColor: '#ffffff',
                    borderRadius: '4px',
                    border: '1px solid #e5e7eb'
                  }}
                >
                  <div style={{ fontSize: '1.2rem', flexShrink: 0 }}>
                    {status === 'complete' ? '✅' : status === 'error' ? '❌' : '📄'}
                  </div>
                  <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                    <Text
                      fontSize="0.75rem"
                      fontWeight="500"
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {file.name}
                    </Text>
                    <Text fontSize="0.65rem" color="#6b7280">
                      {formatFileSize(file.size)} • {status === 'complete' ? 'Complete' : status === 'error' ? 'Failed' : `${progress}%`}
                    </Text>
                  </div>
                  {status === 'uploading' && (
                    <div style={{
                      width: '40px',
                      height: '4px',
                      backgroundColor: '#e5e7eb',
                      borderRadius: '2px',
                      overflow: 'hidden',
                      flexShrink: 0
                    }}>
                      <div style={{
                        width: `${progress}%`,
                        height: '100%',
                        backgroundColor: '#047d95',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {validationError && (
        <Alert variation="error" size="small" isDismissible={true} onDismiss={() => setValidationError(null)}>
          {validationError}
        </Alert>
      )}
      
      {uploadStatus === 'success' && (
        <Alert variation="success" size="small">
          All files uploaded successfully!
        </Alert>
      )}
      
      {uploadStatus === 'success-auto' && (
        <Alert variation="success" size="small">
          Files uploaded to their respective dates!
        </Alert>
      )}
      
      {uploadStatus === 'partial' && (
        <Alert variation="warning" size="small">
          Some files failed to upload
        </Alert>
      )}
      
      {uploadStatus === 'error' && (
        <Alert variation="error" size="small">
          Upload failed. Please try again.
        </Alert>
      )}
      
      <input
        ref={fileInputRef}
        type="file"
        accept=".mov,.mp3,.wav,.mp4,.m4a,video/*,audio/*"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
    </div>
  );
}

export default FileUpload;